"use client"

import { useState, useEffect } from "react";

import Layout from "../components/Layout"
import Card from "../components/Card"
import Button from "../components/Button"
import { Upload, Eye, Send, Image, FileText, X } from "react-feather"
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material"

const ContentManagement = () => {
  const [banners, setBanners] = useState([])
  const [news, setNews] = useState([])
  const [selectedBanner, setSelectedBanner] = useState(null)
  const [bannerPreview, setBannerPreview] = useState("")
  const [newsContent, setNewsContent] = useState({
    title: "",
    content: "",
    summary: "",
  })
  const [previewMode, setPreviewMode] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [actionToConfirm, setActionToConfirm] = useState(null)
  const [bannerUploadLoading, setBannerUploadLoading] = useState(false)
  const [bannerError, setBannerError] = useState(null)
  const [selectedBannerFiles, setSelectedBannerFiles] = useState([])
  const [bannerPreviews, setBannerPreviews] = useState([])
  const [overlayTitles, setOverlayTitles] = useState([])
  const [overlayParagraphs, setOverlayParagraphs] = useState([])
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [overlayEdits, setOverlayEdits] = useState({})
  const [savingBannerId, setSavingBannerId] = useState(null)
  const [updateError, setUpdateError] = useState(null)
  const API_BASE_URL = 'https://api.tenarx.net';
  // Load existing content on component mount
  useEffect(() => {
    fetchNews()
    fetchBanners()
  }, [])

  const fetchNews = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/news`)
      const data = await res.json()
      if (data.success) {
        setNews(data.data)
      } else {
        setNews([])
      }
    } catch (error) {
      setNews([])
    }
  }

  const loadExistingContent = async () => {
    try {
      // In a real app, you would fetch from your API
      // For now, we'll use localStorage to simulate persistence
      const savedBanners = JSON.parse(localStorage.getItem("adminBanners") || "[]")
      const savedNews = JSON.parse(localStorage.getItem("adminNews") || "[]")
      setBanners(savedBanners)
      setNews(savedNews)
    } catch (error) {
      console.error("Error loading content:", error)
    }
  }

  const fetchBanners = async () => {
    try {
      setBannerError(null)
      const res = await fetch(`${API_BASE_URL}/api/banners`)
      const data = await res.json()
      if (data.success) {
        setBanners(data.data)
        // Initialize edit cache
        const init = {}
        data.data.forEach(b => {
          init[b._id] = {
            overlayTitle: b.overlayTitle || "",
            overlayParagraph: b.overlayParagraph || "",
          }
        })
        setOverlayEdits(init)
      } else {
        setBannerError("Failed to load banners")
      }
    } catch (err) {
      setBannerError("Failed to load banners")
    }
  }

  const handleBannerUpload = (event) => {
    const files = Array.from(event.target.files)
    setSelectedBannerFiles(files)
    // Generate previews
    const previews = files.map(file => URL.createObjectURL(file))
    setBannerPreviews(previews)
    // Initialize overlay inputs aligned with files
    setOverlayTitles(new Array(files.length).fill(""))
    setOverlayParagraphs(new Array(files.length).fill(""))
  }

  const handleRemoveBannerFile = (idxToRemove) => {
    setSelectedBannerFiles(prev => prev.filter((_, idx) => idx !== idxToRemove))
    setBannerPreviews(prev => prev.filter((_, idx) => idx !== idxToRemove))
    setOverlayTitles(prev => prev.filter((_, idx) => idx !== idxToRemove))
    setOverlayParagraphs(prev => prev.filter((_, idx) => idx !== idxToRemove))
  }

  const publishBanners = async () => {
    if (!selectedBannerFiles.length) {
      alert("Please select at least one banner image")
      return
    }
    setBannerUploadLoading(true)
    setBannerError(null)
    try {
      const formData = new FormData()
      selectedBannerFiles.forEach((file, idx) => {
        formData.append("banners", file)
        formData.append("names", file.name)
        formData.append("overlayTitles", overlayTitles[idx] || "")
        formData.append("overlayParagraphs", overlayParagraphs[idx] || "")
      })
      const res = await fetch(`${API_BASE_URL}/api/banners`, {
        method: "POST",
        body: formData,
      })
      const data = await res.json()
      if (res.ok && data.success) {
        setSelectedBannerFiles([])
        setBannerPreviews([])
        setOverlayTitles([])
        setOverlayParagraphs([])
        fetchBanners()
        alert("Banners uploaded successfully!")
      } else {
        setBannerError(data.message || "Upload failed")
      }
    } catch (err) {
      setBannerError("Upload error: " + err.message)
    } finally {
      setBannerUploadLoading(false)
    }
  }

  const publishNews = async () => {
    if (!newsContent.title || !newsContent.content) {
      alert("Please fill in both title and content")
      return
    }
    setIsLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/api/news`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: newsContent.title,
          content: newsContent.content,
          summary: newsContent.summary,
        status: "published",
        }),
      })
      const data = await res.json()
      if (res.ok && data.success) {
        setNews(prev => [data.data, ...prev])
      setNewsContent({ title: "", content: "", summary: "" })
      setPreviewMode(false)
      alert("News article published successfully!")
        fetchNews()
      } else {
        alert(data.message || "Failed to publish news")
      }
    } catch (error) {
      console.error("Error publishing news:", error)
      alert("Error publishing news")
    } finally {
      setIsLoading(false)
    }
  }

  const confirmAction = (action) => {
    setActionToConfirm(action)
    setShowConfirm(true)
  }

  const handleConfirmAction = async () => {
    if (actionToConfirm.type === "deleteBanner") {
      deleteBanner(actionToConfirm.id)
    } else if (actionToConfirm.type === "deleteNews") {
      setDeleteLoading(true)
      await deleteNews(actionToConfirm.id)
      setDeleteLoading(false)
    }
    setShowConfirm(false)
  }

  const deleteBanner = async (bannerId) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return
    try {
      await fetch(`${API_BASE_URL}/api/banners/${bannerId}`, { method: "DELETE" })
      setBanners(banners.filter(b => b._id !== bannerId))
    } catch (err) {
      alert("Failed to delete banner")
    }
  }

  const handleEditChange = (id, field, value) => {
    setOverlayEdits(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value }
    }))
  }

  const saveBannerOverlay = async (banner) => {
    try {
      setSavingBannerId(banner._id)
      setUpdateError(null)
      const form = new FormData()
      const edit = overlayEdits[banner._id] || {}
      if (typeof edit.overlayTitle === 'string') form.append('overlayTitle', edit.overlayTitle)
      if (typeof edit.overlayParagraph === 'string') form.append('overlayParagraph', edit.overlayParagraph)
      const res = await fetch(`${API_BASE_URL}/api/banners/${banner._id}`, {
        method: 'PUT',
        body: form
      })
      const data = await res.json()
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to update banner')
      // Update local list
      setBanners(prev => prev.map(b => b._id === banner._id ? data.data : b))
    } catch (e) {
      setUpdateError(e.message)
    } finally {
      setSavingBannerId(null)
    }
  }

  const deleteNews = async (newsId) => {
    try {
      await fetch(`${API_BASE_URL}/api/news/${newsId}`, { method: "DELETE" })
      fetchNews()
    } catch (err) {
      alert("Failed to delete news article")
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <Layout title="Content Management">
      <div className="space-y-6">
        {/* Banner Management Section */}
        <Card title="Advertisement Banners" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upload Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Image className="w-5 h-5" />
                Upload New Banners
              </h3>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  id="banner-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleBannerUpload}
                  className="hidden"
                />
                <label htmlFor="banner-upload" className="cursor-pointer flex flex-col items-center space-y-2">
                  <Upload className="w-8 h-8 text-gray-400" />
                  <span className="text-sm text-gray-600">Click to upload banner images</span>
                  <span className="text-xs text-gray-400">PNG, JPG up to 5MB each, multiple allowed</span>
                </label>
              </div>
              {bannerPreviews.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium">Preview:</h4>
                <div className="space-y-4">
                  {bannerPreviews.map((src, idx) => (
                    <div key={idx} className="border rounded-lg p-3">
                      <div className="flex items-start gap-3">
                        <div className="relative">
                          <img
                            src={src}
                            alt={`Banner preview ${idx + 1}`}
                            className="w-40 h-24 object-cover rounded-lg border"
                          />
                          {/* Live overlay preview */}
                          {(overlayTitles[idx] || overlayParagraphs[idx]) && (
                            <div className="absolute bottom-0 left-0 right-0 p-2 rounded-b-lg bg-gradient-to-t from-black/70 to-transparent">
                              <div className="text-white text-xs font-semibold truncate">
                                {overlayTitles[idx] || 'Title'}
                              </div>
                              {overlayParagraphs[idx] && (
                                <div className="text-white/90 text-[10px] leading-tight line-clamp-2">
                                  {overlayParagraphs[idx]}
                                </div>
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveBannerFile(idx)}
                            className="absolute -top-2 -right-2 bg-white bg-opacity-90 rounded-full p-1 text-red-600 hover:text-red-800 shadow"
                            title="Remove image"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Overlay Title (optional)</label>
                            <input
                              type="text"
                              maxLength={80}
                              value={overlayTitles[idx] || ""}
                              onChange={(e) => setOverlayTitles(prev => prev.map((v, i) => i === idx ? e.target.value : v))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Enter banner title..."
                            />
                            <div className="text-[10px] text-gray-400 mt-1">{(overlayTitles[idx] || '').length}/80</div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Overlay Paragraph (optional)</label>
                            <textarea
                              rows={3}
                              maxLength={240}
                              value={overlayParagraphs[idx] || ""}
                              onChange={(e) => setOverlayParagraphs(prev => prev.map((v, i) => i === idx ? e.target.value : v))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Add short description to appear over the banner..."
                            />
                            <div className="text-[10px] text-gray-400 mt-1">{(overlayParagraphs[idx] || '').length}/240</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={publishBanners}
                  disabled={bannerUploadLoading}
                  className="w-full flex items-center justify-center gap-2 mt-2"
                >
                  <Send className="w-4 h-4" />
                  {bannerUploadLoading ? "Publishing..." : "Publish Banners"}
                </Button>
                {bannerError && <div className="text-red-500 text-sm mt-2">{bannerError}</div>}
              </div>
              )}
            </div>

            {/* Active Banners */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Active Banners ({banners.length})</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {banners.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No banners published yet</p>
                ) : (
                  banners.map((banner) => (
                    <div key={banner._id} className="border rounded-lg p-3 space-y-3">
                      <div className="relative">
                        <img
                          src={`${API_BASE_URL}${banner.image}`}
                          alt={banner.name}
                          className="w-full h-24 object-cover rounded"
                        />
                        {(overlayEdits[banner._id]?.overlayTitle || overlayEdits[banner._id]?.overlayParagraph) && (
                          <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent rounded-b">
                            <div className="text-white text-xs font-semibold truncate">
                              {overlayEdits[banner._id]?.overlayTitle || banner.name}
                            </div>
                            {overlayEdits[banner._id]?.overlayParagraph && (
                              <div className="text-white/90 text-[10px] leading-tight line-clamp-2">
                                {overlayEdits[banner._id]?.overlayParagraph}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Overlay Title</label>
                          <input
                            type="text"
                            maxLength={80}
                            value={overlayEdits[banner._id]?.overlayTitle || ""}
                            onChange={(e) => handleEditChange(banner._id, 'overlayTitle', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter banner title..."
                          />
                          <div className="text-[10px] text-gray-400 mt-1">{(overlayEdits[banner._id]?.overlayTitle || '').length}/80</div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Overlay Paragraph</label>
                          <textarea
                            rows={3}
                            maxLength={240}
                            value={overlayEdits[banner._id]?.overlayParagraph || ""}
                            onChange={(e) => handleEditChange(banner._id, 'overlayParagraph', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Short description to appear over the banner..."
                          />
                          <div className="text-[10px] text-gray-400 mt-1">{(overlayEdits[banner._id]?.overlayParagraph || '').length}/240</div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-xs text-gray-500">{formatDate(banner.publishedAt)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => saveBannerOverlay(banner)}
                            disabled={savingBannerId === banner._id}
                            className="px-3 py-1 text-sm"
                          >
                            {savingBannerId === banner._id ? 'Saving...' : 'Save' }
                          </Button>
                          <button
                            onClick={() => deleteBanner(banner._id)}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Delete banner"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {updateError && <div className="text-red-500 text-xs">{updateError}</div>}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* News Management Section */}
        <Card title="News & Announcements" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Content Creation */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Create News Article
              </h3>

              {!previewMode ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title</label>
                    <input
                      type="text"
                      value={newsContent.title}
                      onChange={(e) => setNewsContent({ ...newsContent, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter news title..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Summary (Optional)</label>
                    <input
                      type="text"
                      value={newsContent.summary}
                      onChange={(e) => setNewsContent({ ...newsContent, summary: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Brief summary..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Content</label>
                    <textarea
                      value={newsContent.content}
                      onChange={(e) => setNewsContent({ ...newsContent, content: e.target.value })}
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Write your news content here..."
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => setPreviewMode(true)}
                      variant="outline"
                      className="flex items-center gap-2"
                      disabled={!newsContent.title || !newsContent.content}
                    >
                      <Eye className="w-4 h-4" />
                      Preview
                    </Button>
                    <Button
                      onClick={publishNews}
                      disabled={isLoading || !newsContent.title || !newsContent.content}
                      className="flex items-center gap-2"
                    >
                      <Send className="w-4 h-4" />
                      {isLoading ? "Publishing..." : "Publish"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="text-xl font-bold mb-2">{newsContent.title}</h4>
                    {newsContent.summary && <p className="text-gray-600 italic mb-3">{newsContent.summary}</p>}
                    <div className="prose max-w-none">
                      {newsContent.content.split("\n").map((paragraph, index) => (
                        <p key={index} className="mb-2">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => setPreviewMode(false)} variant="outline">
                      Edit
                    </Button>
                    <Button onClick={publishNews} disabled={isLoading} className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      {isLoading ? "Publishing..." : "Publish"}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Published News */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Published Articles ({news.length})</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {news.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No articles published yet</p>
                ) : (
                  news.map((article, idx) => (
                    <div key={article._id || article.id || (article.title + (article.publishedAt || idx))} className="border rounded-lg p-3 space-y-2">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">{article.title}</h4>
                          {article.summary && <p className="text-xs text-gray-600 mt-1">{article.summary}</p>}
                          <p className="text-xs text-gray-500 mt-1">{formatDate(article.publishedAt)}</p>
                        </div>
                        <button
                          onClick={() => confirmAction({ type: "deleteNews", id: article._id || article.id })}
                          className="text-red-500 hover:text-red-700 p-1"
                          disabled={deleteLoading}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-700 line-clamp-2">{article.content.substring(0, 100)}...</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
          <DialogTitle>Confirm Action</DialogTitle>
          <DialogContent>
            <p className="text-sm text-gray-700">
              {actionToConfirm?.type === "deleteNews"
                ? "Are you sure you want to delete this news article?"
                : "Are you sure you want to proceed with this action?"}
            </p>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowConfirm(false)} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmAction} color="error" disabled={deleteLoading}>
              {deleteLoading ? "Deleting..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Layout>
  )
}

export default ContentManagement
